// src/data/githubData.js

/**
 * Extracts technologies/skills from README content and categorizes them
 * @param {string} content - README content
 * @returns {Object} Categorized skills
 */
export const extractSkillsFromReadme = (content) => {
  if (!content) return { categories: [], allSkills: [] };
  
  const skills = new Set();
  const categorizedSkills = {
    'Programming Languages': [],
    'Frameworks': [],
    'Databases': [],
    'Tools': [],
    'Others': []
  };
  
  // Map of technology to category and standardized name
  const techMap = {
    // Programming Languages
    'javascript': { category: 'Programming Languages', name: 'JavaScript' },
    'js': { category: 'Programming Languages', name: 'JavaScript' },
    'typescript': { category: 'Programming Languages', name: 'TypeScript' },
    'ts': { category: 'Programming Languages', name: 'TypeScript' },
    'python': { category: 'Programming Languages', name: 'Python' },
    'java': { category: 'Programming Languages', name: 'Java' },
    // 'c#': { category: 'Programming Languages', name: 'C#' },
    // 'csharp': { category: 'Programming Languages', name: 'C#' },
    // 'c++': { category: 'Programming Languages', name: 'C++' },
    // 'cpp': { category: 'Programming Languages', name: 'C++' },
    // 'ruby': { category: 'Programming Languages', name: 'Ruby' },
    // 'go': { category: 'Programming Languages', name: 'Go' },
    // 'golang': { category: 'Programming Languages', name: 'Go' },
    // 'rust': { category: 'Programming Languages', name: 'Rust' },
    // 'swift': { category: 'Programming Languages', name: 'Swift' },
    // 'kotlin': { category: 'Programming Languages', name: 'Kotlin' },
    // 'php': { category: 'Programming Languages', name: 'PHP' },
    'html': { category: 'Programming Languages', name: 'HTML' },
    'css': { category: 'Programming Languages', name: 'CSS' },
    // 'scss': { category: 'Programming Languages', name: 'SCSS' },
    // 'sass': { category: 'Programming Languages', name: 'Sass' },
    // 'less': { category: 'Programming Languages', name: 'Less' },
    'dart': { category: 'Programming Languages', name: 'Dart' },
    // 'shell': { category: 'Programming Languages', name: 'Shell' },
    // 'bash': { category: 'Programming Languages', name: 'Bash' },
    // 'perl': { category: 'Programming Languages', name: 'Perl' },
    // 'lua': { category: 'Programming Languages', name: 'Lua' },
    // 'r': { category: 'Programming Languages', name: 'R' },
    
    // Frameworks
    'react': { category: 'Frameworks', name: 'React' },
    'reactjs': { category: 'Frameworks', name: 'React' },
    // 'vue': { category: 'Frameworks', name: 'Vue.js' },
    // 'vuejs': { category: 'Frameworks', name: 'Vue.js' },
    // 'angular': { category: 'Frameworks', name: 'Angular' },
    // 'angularjs': { category: 'Frameworks', name: 'Angular' },
    'next': { category: 'Frameworks', name: 'Next.js' },
    'nextjs': { category: 'Frameworks', name: 'Next.js' },
    'nuxt': { category: 'Frameworks', name: 'Nuxt.js' },
    'nuxtjs': { category: 'Frameworks', name: 'Nuxt.js' },
    'node': { category: 'Frameworks', name: 'Node.js' },
    'nodejs': { category: 'Frameworks', name: 'Node.js' },
    'express': { category: 'Frameworks', name: 'Express.js' },
    // 'django': { category: 'Frameworks', name: 'Django' },
    'flask': { category: 'Frameworks', name: 'Flask' },
    'fastapi': { category: 'Frameworks', name: 'FastAPI' },
    'spring': { category: 'Frameworks', name: 'Spring Boot' },
    'springboot': { category: 'Frameworks', name: 'Spring Boot' },
    // 'laravel': { category: 'Frameworks', name: 'Laravel' },
    // 'symfony': { category: 'Frameworks', name: 'Symfony' },
    // 'rails': { category: 'Frameworks', name: 'Ruby on Rails' },
    // 'ruby on rails': { category: 'Frameworks', name: 'Ruby on Rails' },
    // 'asp.net': { category: 'Frameworks', name: 'ASP.NET' },
    // 'dotnet': { category: 'Frameworks', name: '.NET' },
    'tailwind': { category: 'Frameworks', name: 'Tailwind CSS' },
    'tailwindcss': { category: 'Frameworks', name: 'Tailwind CSS' },
    'bootstrap': { category: 'Frameworks', name: 'Bootstrap' },
    // 'jquery': { category: 'Frameworks', name: 'jQuery' },
    'flutter': { category: 'Frameworks', name: 'Flutter' },
    // 'react native': { category: 'Frameworks', name: 'React Native' },
    'android': { category: 'Frameworks', name: 'Android' },
    'ios': { category: 'Frameworks', name: 'iOS' },
    // 'graphql': { category: 'Frameworks', name: 'GraphQL' },
    // 'apollo': { category: 'Frameworks', name: 'Apollo' },
    // 'redux': { category: 'Frameworks', name: 'Redux' },
    // 'mobx': { category: 'Frameworks', name: 'MobX' },
    'socket.io': { category: 'Frameworks', name: 'Socket.io' },
    'websocket': { category: 'Frameworks', name: 'WebSocket' },
    'rest': { category: 'Frameworks', name: 'REST API' },
    'restful': { category: 'Frameworks', name: 'REST API' },
    
    // Databases
    'mongodb': { category: 'Databases', name: 'MongoDB' },
    'mongo': { category: 'Databases', name: 'MongoDB' },
    // 'postgresql': { category: 'Databases', name: 'PostgreSQL' },
    // 'postgres': { category: 'Databases', name: 'PostgreSQL' },
    'mysql': { category: 'Databases', name: 'MySQL' },
    // 'redis': { category: 'Databases', name: 'Redis' },
    // 'firebase': { category: 'Databases', name: 'Firebase' },
    // 'firestore': { category: 'Databases', name: 'Firebase Firestore' },
    // 'sqlite': { category: 'Databases', name: 'SQLite' },
    // 'dynamodb': { category: 'Databases', name: 'DynamoDB' },
    // 'cassandra': { category: 'Databases', name: 'Cassandra' },
    // 'elasticsearch': { category: 'Databases', name: 'Elasticsearch' },
    // 'neo4j': { category: 'Databases', name: 'Neo4j' },
    'oracle': { category: 'Databases', name: 'Oracle' },
    'mssql': { category: 'Databases', name: 'MSSQL' },
    'sql server': { category: 'Databases', name: 'SQL Server' },
    
    // Tools & Platforms
    'docker': { category: 'Tools', name: 'Docker' },
    'kubernetes': { category: 'Tools', name: 'Kubernetes' },
    'k8s': { category: 'Tools', name: 'Kubernetes' },
    'aws': { category: 'Tools', name: 'AWS' },
    'azure': { category: 'Tools', name: 'Azure' },
    'gcp': { category: 'Tools', name: 'GCP' },
    'google cloud': { category: 'Tools', name: 'GCP' },
    'git': { category: 'Tools', name: 'Git' },
    'github': { category: 'Tools', name: 'GitHub' },
    'gitlab': { category: 'Tools', name: 'GitLab' },
    'jenkins': { category: 'Tools', name: 'Jenkins' },
    'prisma': { category: 'Tools', name: 'Prisma' },
    'typeorm': { category: 'Tools', name: 'TypeORM' },
    'sequelize': { category: 'Tools', name: 'Sequelize' },
    'mongoose': { category: 'Tools', name: 'Mongoose' },
    'jest': { category: 'Tools', name: 'Jest' },
    'mocha': { category: 'Tools', name: 'Mocha' },
    'chai': { category: 'Tools', name: 'Chai' },
    'cypress': { category: 'Tools', name: 'Cypress' },
    'selenium': { category: 'Tools', name: 'Selenium' },
    'pytest': { category: 'Tools', name: 'Pytest' },
    'junit': { category: 'Tools', name: 'JUnit' },
    'webpack': { category: 'Tools', name: 'Webpack' },
    'vite': { category: 'Tools', name: 'Vite' },
    'babel': { category: 'Tools', name: 'Babel' },
    'eslint': { category: 'Tools', name: 'ESLint' },
    'prettier': { category: 'Tools', name: 'Prettier' },
    'npm': { category: 'Tools', name: 'npm' },
    'yarn': { category: 'Tools', name: 'Yarn' },
    'pnpm': { category: 'Tools', name: 'pnpm' },
    'jwt': { category: 'Tools', name: 'JWT' },
    'oauth': { category: 'Tools', name: 'OAuth' },
  };

  const contentLower = content.toLowerCase();
  
  // Check for explicit tech sections
  const sectionPatterns = [
    /##\s*(?:technologies|tech\s+stack|built\s+with|tools|dependencies|used\s+in\s+this\s+project)/i,
    /###\s*(?:technologies|tech\s+stack|built\s+with|tools|dependencies)/i,
    /<h[2-3]>(?:technologies|tech\s+stack|built\s+with|tools|dependencies)<\/h[2-3]>/i,
  ];

  let techContent = '';
  const lines = content.split('\n');
  let inTechSection = false;
  
  // Extract content from tech sections
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isSectionHeader = sectionPatterns.some(pattern => pattern.test(line));
    
    if (isSectionHeader) {
      inTechSection = true;
      continue;
    }
    
    if (inTechSection) {
      if (/^#{1,3}\s/.test(line) && !sectionPatterns.some(pattern => pattern.test(line))) {
        inTechSection = false;
        continue;
      }
      techContent += line + '\n';
    }
  }

  const searchContent = techContent || content;

  // Find skills in content
  const words = searchContent.split(/[\s,;:.()\[\]{}<>|\\/'"`]+/);
  
  for (const word of words) {
    const normalized = word.toLowerCase().trim();
    for (const [key, value] of Object.entries(techMap)) {
      if (normalized === key || normalized.includes(key)) {
        if (!categorizedSkills[value.category].includes(value.name)) {
          categorizedSkills[value.category].push(value.name);
        }
        skills.add(value.name);
        break;
      }
    }
  }

  // Look for badge patterns
  const badgePatterns = [
    /<img[^>]*src="[^"]*shields\.io\/badge\/[^"]*"[^>]*>/gi,
    /!\[[^\]]*\]\([^)]*shields\.io\/badge\/[^)]*\)/gi,
    /\[!\[[^\]]*\]\([^)]*shields\.io\/badge\/[^)]*\)\]\([^)]*\)/gi,
    /https?:\/\/img\.shields\.io\/badge\/[^\s)>]+/gi,
  ];

  for (const pattern of badgePatterns) {
    const matches = searchContent.match(pattern);
    if (matches) {
      for (const match of matches) {
        const techMatch = match.match(/badge\/([^-]+)-/i) || match.match(/badge\/([^?&/]+)/i);
        if (techMatch) {
          const techName = techMatch[1].replace(/-/g, ' ').trim();
          for (const [key, value] of Object.entries(techMap)) {
            if (techName.toLowerCase().includes(key) || key.includes(techName.toLowerCase())) {
              if (!categorizedSkills[value.category].includes(value.name)) {
                categorizedSkills[value.category].push(value.name);
              }
              skills.add(value.name);
              break;
            }
          }
        }
      }
    }
  }

  // Look for list items with tech
  const listItems = searchContent.match(/^[-*]\s+.+$/gm) || [];
  for (const item of listItems) {
    const itemLower = item.toLowerCase();
    for (const [key, value] of Object.entries(techMap)) {
      if (itemLower.includes(key)) {
        if (!categorizedSkills[value.category].includes(value.name)) {
          categorizedSkills[value.category].push(value.name);
        }
        skills.add(value.name);
        break;
      }
    }
  }

  // Look for code blocks with language indicators
  const codeBlockPatterns = [
    /```(\w+)/g,
    /<code[^>]*class="language-(\w+)"/g,
  ];

  for (const pattern of codeBlockPatterns) {
    let match;
    while ((match = pattern.exec(searchContent)) !== null) {
      const lang = match[1].toLowerCase();
      for (const [key, value] of Object.entries(techMap)) {
        if (lang.includes(key) || key.includes(lang)) {
          if (!categorizedSkills[value.category].includes(value.name)) {
            categorizedSkills[value.category].push(value.name);
          }
          skills.add(value.name);
          break;
        }
      }
    }
  }

  // Remove empty categories
  const categories = Object.entries(categorizedSkills)
    .filter(([_, items]) => items.length > 0)
    .map(([label, items]) => ({
      label,
      items: items.sort().map(name => ({ name }))
    }));

  return {
    categories,
    allSkills: Array.from(skills).sort()
  };
};

/**
 * Fetches the README content for a repository and extracts both description and skills.
 * @param {string} username - GitHub username
 * @param {string} repoName - Repository name
 * @returns {Promise<{description: string, skills: Object}>} Object with description and categorized skills
 */
export const fetchRepoReadme = async (username, repoName) => {
  try {
    const headers = {
      'Accept': 'application/vnd.github.v3+json',
    };
    const token = process.env.REACT_APP_GITHUB_TOKEN;
    if (token) {
      headers['Authorization'] = `token ${token}`;
    }

    const response = await fetch(
      `https://api.github.com/repos/${username}/${repoName}/readme`,
      { headers }
    );

    if (!response.ok) return { description: '', skills: { categories: [], allSkills: [] } };

    const data = await response.json();
    const decoded = atob(data.content.replace(/\n/g, ''));
    
    // Extract skills from README
    const skills = extractSkillsFromReadme(decoded);
    
    // Extract first meaningful paragraph as description
    const lines = decoded.split('\n');
    let description = '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (
        !trimmed ||
        trimmed.startsWith('#') ||
        trimmed.startsWith('![') ||
        trimmed.startsWith('<img') ||
        trimmed.startsWith('<!--') ||
        trimmed.startsWith('[![') ||
        trimmed.match(/^https?:\/\//)
      ) {
        continue;
      }
      const clean = trimmed.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').trim();
      if (clean.length > 20) {
        description = clean;
        break;
      }
    }
    
    description = description.length > 200 ? description.substring(0, 200) + '...' : description;
    
    return { description, skills };
  } catch (error) {
    console.error(`Error fetching README for ${repoName}:`, error);
    return { description: '', skills: { categories: [], allSkills: [] } };
  }
};

/**
 * Fetches public repositories for a given GitHub username, including README descriptions and skills.
 * @param {string} username - GitHub username
 * @param {number} maxRepos - Maximum number of repos to fetch (default: 10)
 * @returns {Promise<Array>} Array of repository objects with skills
 */
export const fetchGitHubRepos = async (username = 'PasinduPerera10', maxRepos = 10) => {
  try {
    const headers = {
      'Accept': 'application/vnd.github.v3+json',
    };
    const token = process.env.REACT_APP_GITHUB_TOKEN;
    if (token) {
      headers['Authorization'] = `token ${token}`;
    }

    const response = await fetch(
      `https://api.github.com/users/${username}/repos?sort=updated&per_page=${maxRepos}&type=public`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
    }

    const repos = await response.json();
    
    const readmePromises = repos.map(repo => fetchRepoReadme(username, repo.name));
    const readmeResults = await Promise.all(readmePromises);

    return repos.map((repo, index) => ({
      id: repo.id,
      name: repo.name,
      description: repo.description || '',
      readmeDescription: readmeResults[index].description || repo.description || 'No description provided',
      skills: readmeResults[index].skills || { categories: [], allSkills: [] },
      html_url: repo.html_url,
      homepage: repo.homepage,
      language: repo.language,
      stars: repo.stargazers_count,
      forks: repo.forks_count,
      topics: repo.topics || [],
      updated_at: repo.updated_at,
      created_at: repo.created_at,
      private: repo.private,
      default_branch: repo.default_branch,
      size: repo.size,
    }));
  } catch (error) {
    console.error('Error fetching GitHub repos:', error);
    return [];
  }
};

/**
 * Gets all unique skills from all repositories
 * @param {string} username - GitHub username
 * @param {number} maxRepos - Maximum number of repos to fetch
 * @returns {Promise<Array<string>>} Array of unique skills
 */
export const getAllSkillsFromRepos = async (username = 'PasinduPerera10', maxRepos = 10) => {
  try {
    const repos = await fetchGitHubRepos(username, maxRepos);
    const allSkills = new Set();
    
    repos.forEach(repo => {
      if (repo.skills && repo.skills.allSkills) {
        repo.skills.allSkills.forEach(skill => allSkills.add(skill));
      }
    });
    
    return Array.from(allSkills).sort();
  } catch (error) {
    console.error('Error getting skills from repos:', error);
    return [];
  }
};

/**
 * Maps a GitHub language field to a standard skill
 */
const languageToSkill = {
  'JavaScript': { category: 'Programming Languages', name: 'JavaScript' },
  'TypeScript': { category: 'Programming Languages', name: 'TypeScript' },
  'Python': { category: 'Programming Languages', name: 'Python' },
  'Java': { category: 'Programming Languages', name: 'Java' },
  'C#': { category: 'Programming Languages', name: 'C#' },
  'C++': { category: 'Programming Languages', name: 'C++' },
  'C': { category: 'Programming Languages', name: 'C' },
  'Ruby': { category: 'Programming Languages', name: 'Ruby' },
  'Go': { category: 'Programming Languages', name: 'Go' },
  'Rust': { category: 'Programming Languages', name: 'Rust' },
  'Swift': { category: 'Programming Languages', name: 'Swift' },
  'Kotlin': { category: 'Programming Languages', name: 'Kotlin' },
  'PHP': { category: 'Programming Languages', name: 'PHP' },
  'Dart': { category: 'Programming Languages', name: 'Dart' },
  'HTML': { category: 'Programming Languages', name: 'HTML' },
  'CSS': { category: 'Programming Languages', name: 'CSS' },
  'Shell': { category: 'Programming Languages', name: 'Shell' },
  'PowerShell': { category: 'Programming Languages', name: 'PowerShell' },
  'Dockerfile': { category: 'Tools', name: 'Docker' },
};

/**
 * Maps GitHub topic strings to standard skills
 */
const topicToSkill = {
  'react': { category: 'Frameworks', name: 'React' },
  'reactjs': { category: 'Frameworks', name: 'React' },
  'nextjs': { category: 'Frameworks', name: 'Next.js' },
  'nodejs': { category: 'Frameworks', name: 'Node.js' },
  'express': { category: 'Frameworks', name: 'Express.js' },
  'expressjs': { category: 'Frameworks', name: 'Express.js' },
  'spring': { category: 'Frameworks', name: 'Spring Boot' },
  'springboot': { category: 'Frameworks', name: 'Spring Boot' },
  'flutter': { category: 'Frameworks', name: 'Flutter' },
  'django': { category: 'Frameworks', name: 'Django' },
  'flask': { category: 'Frameworks', name: 'Flask' },
  'vue': { category: 'Frameworks', name: 'Vue.js' },
  'vuejs': { category: 'Frameworks', name: 'Vue.js' },
  'angular': { category: 'Frameworks', name: 'Angular' },
  'tailwind': { category: 'Frameworks', name: 'Tailwind CSS' },
  'tailwindcss': { category: 'Frameworks', name: 'Tailwind CSS' },
  'bootstrap': { category: 'Frameworks', name: 'Bootstrap' },
  'mongodb': { category: 'Databases', name: 'MongoDB' },
  'postgresql': { category: 'Databases', name: 'PostgreSQL' },
  'mysql': { category: 'Databases', name: 'MySQL' },
  'firebase': { category: 'Databases', name: 'Firebase' },
  'docker': { category: 'Tools', name: 'Docker' },
  'kubernetes': { category: 'Tools', name: 'Kubernetes' },
  'aws': { category: 'Tools', name: 'AWS' },
  'azure': { category: 'Tools', name: 'Azure' },
  'git': { category: 'Tools', name: 'Git' },
  'github': { category: 'Tools', name: 'GitHub' },
  'jenkins': { category: 'Tools', name: 'Jenkins' },
  'api': { category: 'Frameworks', name: 'REST API' },
  'rest': { category: 'Frameworks', name: 'REST API' },
  'graphql': { category: 'Frameworks', name: 'GraphQL' },
  'typescript': { category: 'Programming Languages', name: 'TypeScript' },
  'javascript': { category: 'Programming Languages', name: 'JavaScript' },
  'python': { category: 'Programming Languages', name: 'Python' },
  'java': { category: 'Programming Languages', name: 'Java' },
  'html': { category: 'Programming Languages', name: 'HTML' },
  'css': { category: 'Programming Languages', name: 'CSS' },
  'android': { category: 'Frameworks', name: 'Android' },
  'machine-learning': { category: 'Tools', name: 'Machine Learning' },
  'deep-learning': { category: 'Tools', name: 'Deep Learning' },
  'data-science': { category: 'Tools', name: 'Data Science' },
  'blockchain': { category: 'Tools', name: 'Blockchain' },
  'devops': { category: 'Tools', name: 'DevOps' },
  'ci/cd': { category: 'Tools', name: 'CI/CD' },
  'testing': { category: 'Tools', name: 'Testing' },
};

/**
 * Gets categorized skills from all repositories.
 * Pulls skills from: README content, primary language, and repo topics.
 * @param {string} username - GitHub username
 * @param {number} maxRepos - Maximum number of repos to fetch
 * @returns {Promise<Object>} Categorized skills
 */
export const getCategorizedSkillsFromRepos = async (username = 'PasinduPerera10', maxRepos = 10) => {
  try {
    const repos = await fetchGitHubRepos(username, maxRepos);
    const categoryMap = {
      'Programming Languages': new Set(),
      'Frameworks': new Set(),
      'Databases': new Set(),
      'Tools': new Set(),
      'Others': new Set()
    };
    
    repos.forEach(repo => {
      // 1. Extract skills from README content
      if (repo.skills && repo.skills.categories) {
        repo.skills.categories.forEach(category => {
          if (categoryMap[category.label]) {
            category.items.forEach(item => {
              categoryMap[category.label].add(item.name);
            });
          }
        });
      }

      // 2. Extract skill from repo's primary language
      if (repo.language && languageToSkill[repo.language]) {
        const langSkill = languageToSkill[repo.language];
        if (categoryMap[langSkill.category]) {
          categoryMap[langSkill.category].add(langSkill.name);
        }
      }

      // 3. Extract skills from repo topics
      if (repo.topics && repo.topics.length > 0) {
        repo.topics.forEach(topic => {
          const key = topic.toLowerCase();
          const match = topicToSkill[key] || topicToSkill[key.replace(/[^a-z0-9]/g, '')];
          if (match && categoryMap[match.category]) {
            categoryMap[match.category].add(match.name);
          } else if (match) {
            // Couldn't categorize, put in Others
            categoryMap['Others'].add(topic);
          }
        });
      }
    });
    
    const categories = Object.entries(categoryMap)
      .filter(([_, items]) => items.size > 0)
      .map(([label, items]) => ({
        label,
        items: Array.from(items).sort().map(name => ({ name }))
      }));
    
    return { categories };
  } catch (error) {
    console.error('Error getting categorized skills:', error);
    return { categories: [] };
  }
};